import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

interface NumberingConfig {
  id: string;
  company_id: string;
  project_prefix: string;
  project_digits: number;
  wo_template: string;
  bill_template: string;
  po_template: string;
  vendor_start_number: number;
}

const TEMPLATE_VARS = [
  { token: "{TYPE}", desc: "Project type abbreviation (e.g., FL)" },
  { token: "{PROJECT}", desc: "Project number (e.g., 26001)" },
  { token: "{VENDOR}", desc: "Vendor ID number (e.g., 100)" },
  { token: "{SEQ}", desc: "Sequential number (auto-padded)" },
];

export const NumberingTab = () => {
  const qc = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ["numbering_config"],
    queryFn: async () => {
      const { data, error } = await (supabase.from("numbering_config") as any)
        .select("*")
        .maybeSingle();
      if (error) throw error;
      return data as NumberingConfig | null;
    },
  });

  const [form, setForm] = useState({
    project_prefix: "",
    project_digits: 3,
    wo_template: "WO-{TYPE}-{PROJECT}-{VENDOR}-{SEQ}",
    bill_template: "Bill-{TYPE}-{PROJECT}-{VENDOR}-{SEQ}",
    po_template: "PO-{PROJECT}-{SEQ}",
    vendor_start_number: 1000,
  });

  useEffect(() => {
    if (config) {
      setForm({
        project_prefix: config.project_prefix,
        project_digits: config.project_digits,
        wo_template: config.wo_template,
        bill_template: config.bill_template,
        po_template: config.po_template,
        vendor_start_number: config.vendor_start_number,
      });
    }
  }, [config]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      if (config?.id) {
        const { error } = await (supabase.from("numbering_config") as any)
          .update({
            project_prefix: form.project_prefix,
            project_digits: form.project_digits,
            wo_template: form.wo_template,
            bill_template: form.bill_template,
            po_template: form.po_template,
            vendor_start_number: form.vendor_start_number,
            updated_at: new Date().toISOString(),
          })
          .eq("id", config.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase.from("numbering_config") as any)
          .insert({
            company_id: companyId,
            ...form,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["numbering_config"] });
      toast.success("Numbering settings saved");
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Preview examples
  const year = new Date().getFullYear() % 100;
  const exampleProject = `${form.project_prefix}${year}${"0".repeat(Math.max(0, form.project_digits - 1))}1`;
  const exampleWO = form.wo_template
    .replace("{TYPE}", "FL")
    .replace("{PROJECT}", exampleProject)
    .replace("{VENDOR}", String(form.vendor_start_number))
    .replace("{SEQ}", "01");
  const exampleBill = form.bill_template
    .replace("{TYPE}", "FL")
    .replace("{PROJECT}", exampleProject)
    .replace("{VENDOR}", String(form.vendor_start_number))
    .replace("{SEQ}", "01");
  const examplePO = form.po_template
    .replace("{PROJECT}", exampleProject)
    .replace("{SEQ}", "001");

  if (isLoading) {
    return <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Document Numbering</CardTitle>
          <CardDescription>
            Customize how project numbers, work orders, bills, and purchase orders are numbered across your company.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Project Numbers */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Project Numbers</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Prefix (optional)</Label>
                <Input
                  value={form.project_prefix}
                  onChange={e => setForm(f => ({ ...f, project_prefix: e.target.value }))}
                  placeholder="e.g. P- (leave empty for year-only)"
                />
                <p className="text-xs text-muted-foreground">Added before the year digits</p>
              </div>
              <div className="space-y-1">
                <Label>Sequence Digits</Label>
                <Input
                  type="number"
                  min={2}
                  max={5}
                  value={form.project_digits}
                  onChange={e => setForm(f => ({ ...f, project_digits: parseInt(e.target.value) || 3 }))}
                />
                <p className="text-xs text-muted-foreground">Zero-padded sequence length</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Preview:</span>
              <Badge variant="outline" className="font-mono">{exampleProject}</Badge>
            </div>
          </div>

          <Separator />

          {/* Vendor IDs */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Vendor ID Numbers</h3>
            <div className="max-w-xs space-y-1">
              <Label>Starting Number</Label>
              <Input
                type="number"
                min={1}
                value={form.vendor_start_number}
                onChange={e => setForm(f => ({ ...f, vendor_start_number: parseInt(e.target.value) || 100 }))}
              />
              <p className="text-xs text-muted-foreground">New vendors auto-assigned from this number. Existing vendor IDs are never reused.</p>
            </div>
          </div>

          <Separator />

          {/* Templates */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Number Templates</h3>
            <div className="text-xs text-muted-foreground space-y-1 bg-muted/50 rounded-md p-3">
              <p className="font-medium">Available tokens:</p>
              {TEMPLATE_VARS.map(v => (
                <p key={v.token}><code className="font-mono text-foreground">{v.token}</code> — {v.desc}</p>
              ))}
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Work Order Template</Label>
                <Input
                  value={form.wo_template}
                  onChange={e => setForm(f => ({ ...f, wo_template: e.target.value }))}
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Preview:</span>
                  <Badge variant="outline" className="font-mono text-xs">{exampleWO}</Badge>
                </div>
              </div>

              <div className="space-y-1">
                <Label>Bill Template</Label>
                <Input
                  value={form.bill_template}
                  onChange={e => setForm(f => ({ ...f, bill_template: e.target.value }))}
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Preview:</span>
                  <Badge variant="outline" className="font-mono text-xs">{exampleBill}</Badge>
                </div>
              </div>

              <div className="space-y-1">
                <Label>Purchase Order Template</Label>
                <Input
                  value={form.po_template}
                  onChange={e => setForm(f => ({ ...f, po_template: e.target.value }))}
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Preview:</span>
                  <Badge variant="outline" className="font-mono text-xs">{examplePO}</Badge>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              <Save className="h-4 w-4 mr-1" />
              Save Numbering Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
